const SCHEMA_CONTEXT = 'https://schema.org';

export function buildOrganizationJsonLd({ siteUrl, logoUrl }) {
  if (!siteUrl) return null;
  return {
    '@context': SCHEMA_CONTEXT,
    '@type': 'Organization',
    name: 'Charlee.ai',
    url: siteUrl,
    ...(logoUrl ? { logo: logoUrl } : {}),
  };
}

export function buildWebSiteJsonLd({ siteUrl }) {
  if (!siteUrl) return null;
  return {
    '@context': SCHEMA_CONTEXT,
    '@type': 'WebSite',
    name: 'Charlee.ai',
    url: siteUrl,
  };
}

export function buildArticleJsonLd({
  headline,
  description,
  url,
  image,
  datePublished,
  dateModified,
  author,
  authorType = 'Person',
  publisher,
  isPartOf,
}) {
  if (!headline || !url) return null;
  const article = {
    '@context': SCHEMA_CONTEXT,
    '@type': 'Article',
    headline,
    mainEntityOfPage: { '@type': 'WebPage', '@id': url },
    url,
  };
  if (description) article.description = description;
  if (image) article.image = [image];
  if (datePublished) article.datePublished = datePublished;
  if (dateModified) article.dateModified = dateModified;
  if (author) {
    article.author = {
      '@type': authorType === 'Organization' ? 'Organization' : 'Person',
      name: author,
    };
  }
  if (publisher) {
    article.publisher = {
      '@type': 'Organization',
      name: publisher,
    };
  }
  if (isPartOf?.name) {
    article.isPartOf = {
      '@type': isPartOf.type || 'CreativeWork',
      name: isPartOf.name,
      ...(isPartOf.url ? { url: isPartOf.url } : {}),
    };
  }
  return article;
}

export function buildBreadcrumbJsonLd(items) {
  if (!Array.isArray(items) || items.length === 0) return null;
  const withUrls = items.filter((item) => item?.name && item?.url);
  if (withUrls.length === 0) return null;
  return {
    '@context': SCHEMA_CONTEXT,
    '@type': 'BreadcrumbList',
    itemListElement: withUrls.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: item.url,
    })),
  };
}

export function buildFaqPageJsonLd({ url, questions }) {
  if (!url || !Array.isArray(questions) || questions.length === 0) return null;
  const mainEntity = questions
    .map((item) => {
      const question = typeof item?.question === 'string' ? item.question.trim() : '';
      const answer = typeof item?.answer === 'string' ? item.answer.trim() : '';
      if (!question || !answer) return null;
      return {
        '@type': 'Question',
        name: question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: answer,
        },
      };
    })
    .filter(Boolean);
  if (mainEntity.length === 0) return null;
  return {
    '@context': SCHEMA_CONTEXT,
    '@type': 'FAQPage',
    mainEntity,
    url,
  };
}

export function buildDefinedTermJsonLd({
  name,
  description,
  url,
  termSetName,
  termSetUrl,
}) {
  if (!name || !url) return null;
  const term = {
    '@context': SCHEMA_CONTEXT,
    '@type': 'DefinedTerm',
    name,
    url,
  };
  if (description) term.description = description;
  if (termSetName) {
    term.inDefinedTermSet = {
      '@type': 'DefinedTermSet',
      name: termSetName,
      ...(termSetUrl ? { url: termSetUrl } : {}),
    };
  }
  return term;
}

export function buildBookJsonLd({
  name,
  description,
  url,
  image,
  dateModified,
  numberOfPages,
  author = 'Charlee.ai',
}) {
  if (!name || !url) return null;
  const book = {
    '@context': SCHEMA_CONTEXT,
    '@type': 'Book',
    name,
    url,
    author: { '@type': 'Organization', name: author },
  };
  if (description) book.description = description;
  if (image) book.image = image;
  if (dateModified) book.dateModified = dateModified;
  if (numberOfPages) book.numberOfPages = numberOfPages;
  return book;
}

export function buildChapterJsonLd({
  name,
  description,
  url,
  image,
  dateModified,
  position,
  bookName,
  bookUrl,
  author = 'Charlee.ai',
}) {
  if (!name || !url) return null;
  const chapter = {
    '@context': SCHEMA_CONTEXT,
    '@type': 'Chapter',
    name,
    url,
    author: { '@type': 'Organization', name: author },
  };
  if (description) chapter.description = description;
  if (image) chapter.image = image;
  if (dateModified) chapter.dateModified = dateModified;
  if (position != null) chapter.position = position;
  if (bookName) {
    chapter.isPartOf = {
      '@type': 'Book',
      name: bookName,
      ...(bookUrl ? { url: bookUrl } : {}),
    };
  }
  return chapter;
}

export function compactJsonLd(graph) {
  return graph.filter(Boolean);
}
